import getQueryParameter from '../helper/get-query-parameter';
import { getGlobals, setGlobals } from '../globals';
import { page, track, trackQuantityEvent, trackUniqueEvent } from './v4.calls';
import events from './events';
import { getVisitorProperties, updateVisitorProperties } from './v4.visitor-properties';
import psProperties from './v4.ps-properties';
import { getNowISO } from './helper';
import { debugTracking } from '../../debug';
import { setTrackingProperty } from '../../helper';

export const campaignLoad = (pageSlug: string, pageName: string) => {
    debugTracking('-> V4 campaignLoad', pageSlug, pageName);

    const visitorProperties = getVisitorProperties();

    // already loaded
    if (visitorProperties[psProperties.startSlug]) {
        return;
    }
    const { utm, custom, thirdParty, additionalTrackingTitles } = getQueryParameter();

    const globals = getGlobals();

    setGlobals({
        ...globals,
        trackingTitles: {
            ...globals.trackingTitles,
            ...additionalTrackingTitles,
        },
    });

    trackQuantityEvent(events.campaignLoad, {
        pageSlug,
        utm,
        custom,
        thirdParty,
    });

    updateVisitorProperties({
        // adds utm params to traits
        ...utm,
        // adds custom params to traits
        ...custom,
        // adds third party params (like google click id [?gclid=xxx]) to traits
        ...thirdParty,

        [psProperties.startSlug]: pageSlug,
        [psProperties.startPage]: pageName,
        ...utm,
        ...thirdParty,
        ...custom,
    });
};

export const pageLoad = (
    pageId: string,
    pageName: string,
    pageSlug: string,
    isLastPage: boolean,
) => {
    debugTracking('-> V4 pageLoad', pageId, pageName, pageSlug, isLastPage);

    const properties = {
        pageId,
        pageName,
        pageSlug,
        isLastPage,
    };
    campaignLoad(pageSlug, pageName);
    page(pageName, properties);
    // TODO CF: find out if we need this
    // trackQuantityEvent(events.pageLoad, properties);
    //
    trackUniqueEvent(`${events.pageLoad}: ${pageSlug}`, properties);
    const visitorProperties = getVisitorProperties();
    // only "completes" when NOT started on last page
    //
    if (isLastPage && visitorProperties[psProperties.startSlug] !== pageSlug) {
        campaignComplete(pageName);
    }
};

export const buttonClick = (buttonClickId: string) => {
    debugTracking('-> V4 buttonClick', buttonClickId);

    const properties = {
        clickId: buttonClickId,
    };

    trackQuantityEvent(events.buttonClick, properties);
    trackUniqueEvent(`${events.buttonClick}: ${buttonClickId}`, properties);
};

export const cookiesAccept = (integrations: string[]) => {
    debugTracking('-> V4 cookiesAccept', integrations);

    trackQuantityEvent(events.cookiesAccept, {
        integrations,
    });

    updateVisitorProperties({ [psProperties.cookies]: integrations.sort().join('; ') });
};

interface AnswerClickedQuestion {
    text: string;
    fieldName: string;
}
interface AnswerClickedAnswer {
    text: string;
    clickId: string;
}
export const answerClicked = (
    question: AnswerClickedQuestion,
    answer: AnswerClickedAnswer,
    campaignId?: string,
    uniqueEventPrefix: string = events.answerClick,
) => {
    debugTracking('-> V4 answerClicked', question, answer);

    const properties = {
        questionText: question.text,
        answerText: answer.text,
        fieldName: question.fieldName,
    };

    trackQuantityEvent(events.answerClick, properties);
    trackUniqueEvent(`${uniqueEventPrefix}: ${answer.clickId}`, properties);
    updateVisitorProperties({ [question.fieldName]: answer.text });

    // For personalization
    setTrackingProperty(question.fieldName, answer.text, campaignId);
};

export const formSubmit = (formValues: any, submitButtonClickId: string) => {
    debugTracking('-> V4 formSubmit', formValues, submitButtonClickId);

    trackQuantityEvent(events.formSubmit, {
        ...formValues,
    });

    trackUniqueEvent(`${events.buttonClick}: ${submitButtonClickId}`, {
        clickId: submitButtonClickId,
    });

    updateVisitorProperties(formValues);
};

export const multipleChoiceSubmit = (
    question: AnswerClickedQuestion,
    answer: AnswerClickedAnswer,
    campaignId: string,
) => {
    debugTracking('-> V4 answerClicked', question, answer);

    answerClicked(question, answer, campaignId, events.buttonClick);
};

export const convert = () => {
    debugTracking('-> V4 convert');

    track(events.convert, {
        ...getVisitorProperties(),
    });
};

export const campaignComplete = (pageName: string) => {
    debugTracking('-> V4 campaignComplete', pageName);

    updateVisitorProperties({
        [psProperties.isCompleted]: true,
        [psProperties.completedAt]: getNowISO(),
        [psProperties.result]: pageName,
    });

    track(events.campaignComplete, {
        ...getVisitorProperties(),
    });
};

export const campaignClose = () => {
    debugTracking('-> V4 campaignClose');

    updateVisitorProperties({
        [psProperties.isClosed]: true,
        [psProperties.closedAt]: getNowISO(),
        [psProperties.endSlug]: getGlobals().perspectivePageSlug,
    });

    track(events.campaignClose, {
        ...getVisitorProperties(),
    });
};

export const calendlyBooking = () => {
    debugTracking('-> V4 calendlyBooking');

    trackQuantityEvent(events.calendlyBook);

    updateVisitorProperties({
        [psProperties.calendlyBook]: true,
    });
};
